import { useEffect, useRef } from 'react';
import { useDispatch } from 'react-redux';
import * as yup from 'yup';

import { filterActions, categoryMappingActions } from '../reducer';
import { Segment } from '../../../utils/segments';
import { parseUrlParams, ensureStringArray } from '../../../utils/urlParams';
import { useAppSelector } from '../../../app/store';
import {
  selectCategoryMappingIsReady,
  selectPendingCategorySlugs,
  selectSlugToIdMap,
  slugsToIds,
} from '../selectors';

const filterSchema = yup.object({
  page: yup.number().integer().positive(),
  size: yup.number().integer().positive(),
  segment: yup.string().oneOf(Object.values(Segment)),
  sortBy: yup.string().oneOf(['DOCUMENT_NAME', 'DOCUMENT_DATE']).optional(),
  sortDirection: yup.mixed<'ASC' | 'DESC'>().oneOf(['ASC', 'DESC']).optional(),
  contractNumbers: yup.array().of(yup.string().required()).optional(),
  products: yup.array().of(yup.string().required()).optional(),
  categories: yup.array().of(yup.string().required()).optional(),
  createdFrom: yup.string().optional(),
  createdTo: yup.string().optional(),
});

/**
 * Hook that parses URL search params and dispatches them to the filter Redux state.
 */
export default function useUrlFilterSync() {
  const dispatch = useDispatch();
  const hasProcessedCategoriesRef = useRef(false);

  const isCategoryMappingReady = useAppSelector(selectCategoryMappingIsReady);
  const pendingCategorySlugs = useAppSelector(selectPendingCategorySlugs);
  const slugToIdMap = useAppSelector(selectSlugToIdMap);

  useEffect(() => {
    const parsedParams = parseUrlParams(window.location.search);
    const normalizedParams = {
      ...parsedParams,
      contractNumbers: ensureStringArray(parsedParams.contractNumbers),
      products: ensureStringArray(parsedParams.products),
      categories: ensureStringArray(parsedParams.categories),
    };

    try {
      const validFilter = filterSchema.validateSync(normalizedParams, {
        stripUnknown: true,
      });

      if (validFilter.categories?.length) {
        dispatch(
          categoryMappingActions.setPendingCategorySlugs(validFilter.categories)
        );
      }

      const { categories, ...filters } = validFilter;
      dispatch(filterActions.set(filters));
    } catch (error) {
      if (error instanceof yup.ValidationError) {
        console.error('Filter validation error:', error.errors);
      }
    }
  }, [dispatch]);

  // convert category slugs to IDs once mapping is ready
  useEffect(() => {
    if (
      isCategoryMappingReady &&
      pendingCategorySlugs &&
      pendingCategorySlugs.length > 0 &&
      !hasProcessedCategoriesRef.current
    ) {
      const categoryIds = slugsToIds(slugToIdMap, pendingCategorySlugs);
      if (categoryIds.length > 0) {
        dispatch(filterActions.changeCategories(categoryIds));
      }
      dispatch(categoryMappingActions.clearPendingCategorySlugs());
      hasProcessedCategoriesRef.current = true;
    }
  }, [isCategoryMappingReady, pendingCategorySlugs, dispatch, slugToIdMap]);
}
